import { useMemo } from "preact/hooks";
import type { CoreData, NameEntry, SexIdx, SexPref } from "../data/types";
import type { HideType } from "../state/app";
import { hiddenType, inDeckFor } from "../state/deck";
import { originCounts, originExamples } from "../data/origins";
import { fmt, sexIdxFor } from "../data/format";

export const HIDE_TYPES: { id: HideType; label: string; short: string }[] = [
  { id: "joined", label: "Double-barrelled names", short: "double-barrelled names" },
  { id: "alt", label: "Other spellings", short: "other spellings" },
  { id: "short", label: "Nicknames and short forms", short: "nicknames" },
  { id: "unisex", label: "Names for boys and girls", short: "unisex names" },
  { id: "apostrophe", label: "Names with an apostrophe", short: "names with apostrophes" },
];

export function hideShort(h: HideType): string {
  return HIDE_TYPES.find((t) => t.id === h)?.short ?? h;
}

export interface HideStat {
  n: number;
  /** the best-known examples in this deck */
  ex: string[];
}

/** How many names each kind of hiding would take out of the deck, with familiar examples. */
export function hideStats(core: CoreData, sex: SexPref): Record<HideType, HideStat> {
  const out = {} as Record<HideType, HideStat & { best: NameEntry[] }>;
  for (const t of HIDE_TYPES) out[t.id] = { n: 0, ex: [], best: [] };
  const rank = (e: NameEntry) => {
    const si: SexIdx = sexIdxFor(e, sex);
    return e.r[si] || 1e9;
  };
  for (const e of core.entries) {
    if (!inDeckFor(e, sex)) continue;
    for (const t of HIDE_TYPES) {
      if (!hiddenType(e, [t.id])) continue;
      const s = out[t.id];
      s.n++;
      if (s.best.length < 3 || rank(e) < rank(s.best[2])) {
        s.best = [...s.best, e].sort((a, b) => rank(a) - rank(b)).slice(0, 3);
      }
    }
  }
  for (const t of HIDE_TYPES) out[t.id].ex = out[t.id].best.map((e) => e.name);
  return out;
}

/** Every origin in the deck, each with "only these" and "hide these" switches. */
export function OriginList({
  core,
  sex,
  only,
  hide,
  onChange,
}: {
  core: CoreData;
  sex: SexPref;
  only: string[];
  hide: string[];
  onChange: (only: string[], hide: string[]) => void;
}) {
  const counts = useMemo(() => originCounts(core, (e) => inDeckFor(e, sex)), [core, sex]);
  const examples = useMemo(() => originExamples(core, (e) => inDeckFor(e, sex), (e) => e.r[sexIdxFor(e, sex)], 2), [core, sex]);
  const without = (list: string[], o: string) => list.filter((x) => x !== o);
  return (
    <ul class="rows origin-pick">
      {counts.map(([o, n]) => {
        const isOnly = only.includes(o);
        const isHidden = hide.includes(o);
        return (
          <li key={o} class={`op-row ${isHidden ? "is-hidden" : ""}`}>
            <span class="op-text">
              <span class="op-name">{o}</span>
              <span class="op-count">
                {fmt(n)} names{examples.has(o) ? `, like ${examples.get(o)!.join(" and ")}` : ""}
              </span>
            </span>
            <button
              class={`op-btn ${isOnly ? "on" : ""}`}
              aria-pressed={isOnly}
              aria-label={`Only ${o} names`}
              onClick={() => onChange(isOnly ? without(only, o) : [...only, o], without(hide, o))}
            >
              Only
            </button>
            <button
              class={`op-btn op-hide ${isHidden ? "on" : ""}`}
              aria-pressed={isHidden}
              aria-label={`Hide ${o} names`}
              onClick={() => onChange(without(only, o), isHidden ? without(hide, o) : [...hide, o])}
            >
              Hide
            </button>
          </li>
        );
      })}
    </ul>
  );
}
